import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { DatePipe } from '@angular/common';
import { Options } from 'angular2-csv';
import { AppState } from '@app/store/app.state';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { CaseModel } from '@app/core/models/case.model';
import { CaseActions, CaseSelectors, GlobalSelectors } from '@app/store';
import { FormBuilder, FormGroup } from '@angular/forms';
import moment from 'moment';

@Component({
  selector: 'app-support-ticket-list',
  templateUrl: './support-ticket-list.component.html',
  styleUrls: ['./support-ticket-list.component.scss'],
})
export class SupportTicketListComponent implements OnInit, OnDestroy {
  // handles all component subscriptions
  subscriptions: Subscription = new Subscription();
  supportCases$: Observable<CaseModel[]> = this.store.select(
    CaseSelectors.getFormattedCases
  );
  isCasesLoading$: Observable<boolean> = this.store.select(
    CaseSelectors.isCasesLoading
  );
  caseTypes = {};
  searchValue = '';
  csvData = null;
  csvReportDate = this.datePipe
    .transform(new Date(), 'MM/dd/yyyy')
    .replace('/', '_');

  csvReportOptions: Options = {
    filename: '',
    title: '',
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalseparator: '.',
    showLabels: false,
    headers: [
      'Status',
      'Case Number',
      'Case Type',
      'Case Summary',
      'Created On',
      'Modified On',
      'Description',
    ],
    showTitle: false,
    useBom: false,
    removeNewLines: true,
    keys: [
      'stateCodeString',
      'caseNumber',
      'caseTypeCodeString',
      'title',
      'createdDateFormatted',
      'modifiedDateFormatted',
      'description',
    ],
  };

  dateRangeForm: FormGroup;
  maxDate: Date;
  startDate: Date;
  endDate: Date;
  isDateRangeValid: boolean = true;

  constructor(
    private store: Store<AppState>,
    private datePipe: DatePipe,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    const today = new Date();

    today.setHours(23, 59, 59, 0);

    let startDate = moment().subtract(90, 'days').toDate();

    if (startDate) {
      const selectedStartDate = new Date(startDate);

      selectedStartDate.setHours(0, 0, 0, 0);

      startDate = selectedStartDate;
    }

    this.dateRangeForm = this.fb.group({
      startDate: [startDate],
      endDate: [today],
    });

    this.maxDate = today;
    this.startDate = startDate;
    this.endDate = today;

    this.getCases();

    this.dateRangeForm.get('endDate')?.valueChanges.subscribe(endDate => {
      let startDate = this.dateRangeForm.get('startDate')?.value;

      // Check if both startDate and endDate are selected
      if (startDate && endDate) {
        const diffInDays = moment(endDate).diff(moment(startDate), 'days');

        this.isDateRangeValid = diffInDays <= 90 && diffInDays >= 0;

        const selectedStartDate = new Date(startDate);

        selectedStartDate.setHours(0, 0, 0, 0);
        startDate = selectedStartDate;

        const selectedEndDate = new Date(endDate);

        selectedEndDate.setHours(23, 59, 59, 0);
        endDate = selectedEndDate;

        if (this.isDateRangeValid) {
          this.startDate = startDate;
          this.endDate = endDate;
          this.getCases();
        }
      }
    });
  }

  getCases(): void {
    this.subscriptions.add(
      this.store
        .select(GlobalSelectors.getSelectedGlobalFilters)
        .pipe(
          filter(
            selectedBillingAcc => selectedBillingAcc.billingAccounts.length > 0
          ),
          distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) === JSON.stringify(curr);
          })
        )
        .subscribe(selectedBillingAcc => {
          this.store.dispatch(CaseActions.clearCases());
          this.store.dispatch(CaseActions.casesStartLoading());
          const requestBody = {
            ...selectedBillingAcc,
            startDate: this.startDate.toISOString(),
            endDate: this.endDate.toISOString(),
          };

          this.store.dispatch(
            CaseActions.getCases({ requestBody: requestBody })
          );
          this.formatCsvData();
        })
    );
  }

  formatCsvData(): void {
    // format the created date for the csv report
    this.subscriptions.add(
      this.store
        .select(CaseSelectors.getFormattedCases)
        .pipe(
          filter(response => response && response.length > 0),
          map(allCases =>
            allCases.map(theCase => ({
              ...theCase,
              createdDateFormatted: this.datePipe.transform(
                theCase.createdDate,
                'MM/dd/yyyy'
              ),
              modifiedDateFormatted: this.datePipe.transform(
                theCase.modifiedDate,
                'MM/dd/yyyy'
              ),
            }))
          )
        )
        .subscribe(csvData => (this.csvData = csvData))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
